import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { DynamicFormService } from '@ntap/common';
import {
  AttributeStateService,
  BackendDataSourceService,
  CoreConfig,
  CoreConfigService,
  InputControlData,
  Item,
  ItemView,
  ItemViewProviderDirective,
  ListView,
  ListViewProviderDirective,
  NtapGlobalConstants,
  UIComponents,
  Util,
  View
} from '@ntap/core';

import { ColumnInfo, PrimeNGTableComponent, TableMeta } from '@ntap/primeng';
import { SortEvent } from 'primeng/api';
import { Button } from 'primeng/button';
import { OverlayPanel } from 'primeng/overlaypanel';
import {
  combineLatest,
  delay,
  filter,
  first,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap
} from 'rxjs';

export type TableMetaWithDataKey = TableMeta & {
  dataKey: string;
  showHeader: boolean;
  view: View;
};

@Component({
  selector: 'app-editable-table',
  templateUrl: 'editable-table.component.html',
  styleUrls: ['./editable-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditableTableComponent
  extends PrimeNGTableComponent
  implements OnInit, AfterViewInit, AfterContentInit
{
  INIT_COMP_ID: string = UIComponents.INPUT_DROPDOWN;

  public inputs$: Observable<InputControlData[]>;
  public item$: Observable<Item>;

  tableMetaWithDataKey$: Observable<TableMetaWithDataKey>;
  clonedItems: { [s: string]: Item } = {};
  addRowCounter = 0;

  @ViewChild(OverlayPanel)
  overlayPanel: OverlayPanel;

  @ViewChild('overlayButton')
  overlayButton: Button;

  @Input()
  public parentItem: any;

  @Input()
  public relToParentItemKey = 'parent_id';

  @Input()
  public readOnly = false;

  @Input()
  public showInOverlay = false;

  private startingRowInitialized = false;

  private panelOpen = new Subject<boolean>();

  constructor(
    protected override listViewProvider: ListViewProviderDirective<ListView>,
    @Inject(CoreConfigService) protected override coreConfig: CoreConfig,
    protected override attributeStateService: AttributeStateService,
    protected backendDataSource: BackendDataSourceService,
    protected formService: DynamicFormService,
    protected itemViewProvider: ItemViewProviderDirective<ItemView>
  ) {
    super(listViewProvider, coreConfig, attributeStateService);
  }

  ngOnInit() {
    this.tableMetaWithDataKey$ = combineLatest([
      this.listViewProvider.view$,
      this.tableMeta$
    ]).pipe(
      map(([view, tableMeta]) => {
        const idAttr = Util.getFullAttrName(
          view.$mainType,
          NtapGlobalConstants.ATTR_id
        );
        tableMeta.columns.forEach((column: any) => {
          column.searchable =
            view.searchable_columns.indexOf(column.name) !== -1;
        });
        const showHeader =
          tableMeta.columns.some((c: any) => c.searchable) ||
          tableMeta.columns.some((c: ColumnInfo) => c.sortable);
        const metaWithDataKey = Object.assign(
          { dataKey: idAttr, showHeader: showHeader, view: view },
          tableMeta
        );
        return metaWithDataKey;
      })
    );
  }

  // TODO ovo bi trebalo u zasebnu komponentu koja extenda ovu. Ili da se "izvana" nekako okine taj reload u kontekstu gdje je dostupan listViewProvider kojeg komponenta koristi
  ngAfterContentInit() {
    this.addSubscription(
      combineLatest([this.listViewProvider.view$, this.itemViewProvider.item$])
        .pipe(
          delay(0),
          filter(
            ([view]) =>
              (view as any)['name'] == 'en_jedinica_gradiva_primatelj' ||
              (view as any)['name'] == 'en_jedinica_gradiva_posiljatelj'
          ),
          switchMap(() => {
            return this.formService.formGroup$.pipe(
              switchMap((fg) => {
                const kanalDostave =
                  fg.get('seup~pismeno~kanal_dostave') ||
                  ({ valueChanges: of(null) } as AbstractControl);
                return kanalDostave.valueChanges;
              })
            );
          })
        )
        .subscribe((kanal) => {
          (this.listViewProvider.viewState as any)['$reload'] = true;
          (this.listViewProvider.viewState as any)[
            'seup~pismeno~kanal_dostave'
          ] = kanal;
          this.listViewProvider.reload();
        })
    );
  }

  ngAfterViewInit(): void {
    this.autoInitializeFirstRow();
  }

  private autoInitializeFirstRow() {
    combineLatest([this.tableMetaWithDataKey$, this.tableData$, this.panelOpen])
      .pipe(
        first(),
        tap(([metaWithDataKey]) => {
          if (
            !this.startingRowInitialized &&
            !(
              metaWithDataKey.viewName === 'en_jedinica_gradiva_mjesto' ||
              metaWithDataKey.viewName === 'en_jedinica_gradiva_autor' ||
              metaWithDataKey.viewName ===
                'en_jedinica_gradiva_povezane_jedinice_gradiva'
            )
          ) {
            this.onAddNewItem(metaWithDataKey);
            this.startingRowInitialized = true;
          }
        })
      )
      .subscribe();
  }

  onAddNewItem(tableMeta: TableMetaWithDataKey) {
    this.listViewProvider.view$
      .pipe(
        first(),
        switchMap((view) => {
          const newItemState: any = {
            [tableMeta.dataKey]: 0,
            [NtapGlobalConstants.ATTR_$itemType]: view.$mainType
          };
          const newItem: any = {};
          return this.backendDataSource
            .getViewData('$defaultItemView', newItemState)
            .pipe(
              tap((defaultItems: any[]) => {
                if (!this.table) {
                  return;
                }
                defaultItems.forEach((defaultItem) => {
                  view.attributes.forEach((attr: any) => {
                    newItem[attr.name] = defaultItem[attr.name];
                  });
                  newItem[tableMeta.dataKey] = this.addRowCounter;
                  newItem[NtapGlobalConstants.ATTR_$itemType] = view.$mainType;
                });
                this.addRowCounter = this.addRowCounter - 1;
                this.listViewProvider.addItem(newItem);
                this.table.initRowEdit(newItem);
                this.table.cd.markForCheck();
                this.table.cd.detectChanges();
              })
            );
        })
      )
      .subscribe();
  }

  togglePanel(event: Event) {
    this.overlayPanel.toggle(event);
    this.overlayButton.icon =
      this.overlayButton.icon && this.overlayButton.icon?.indexOf('plus') > 0
        ? 'pi pi-minus-circle'
        : 'pi pi-plus-circle';
    this.panelOpen.next(this.overlayPanel.overlayVisible);
  }

  handleSort(event: SortEvent): void {
    const field = event.field;
    const order = event.order;

    if (field && order) {
      this.tableData$.pipe(first()).subscribe((tableData) => {
        const sortedItems = [...tableData.tableItems].sort((a: any, b: any) => {
          const valueA = a[field] ?? '';
          const valueB = b[field] ?? '';

          // If both are numbers, compare numerically
          if (typeof valueA === 'number' && typeof valueB === 'number') {
            return order * (valueA - valueB);
          }

          return (
            order *
            String(valueA).localeCompare(String(valueB), 'hr', {
              sensitivity: 'base'
            })
          );
        });

        tableData.tableItems = sortedItems;

        this.table.value = tableData.items = sortedItems;
        this.table.cd.markForCheck();
      });
    }
  }
}
